import {postAdminAdjustment} from "@/lib/adminAdjustment";
import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  MessageSquare, Settings, BarChart2, Ban, Send, FileText,
  Plus, Trash2, RefreshCw, ChevronLeft, ChevronRight, Store,
  ToggleLeft, ToggleRight, CheckCircle, XCircle, AlertCircle, Loader2,
} from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }
function fmtDate(s: string) {
  return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

type Tab = "settings" | "stores" | "senders" | "opt-outs" | "campaigns" | "log" | "stats";

interface PlatformSettings {
  id: number;
  enabled: boolean;
  costPerSmsPence: number;
  sellPerSmsPence: number;
  smsVerificationEnabled: boolean;
  orderConfirmationEnabled: boolean;
  marketingEnabled: boolean;
  minimumTopupPence: number;
  maximumDebtPence: number;
  /** Null when no Esendex rate has been entered — previews then say "cost unknown". */
  esendexPricePerSegmentMillipence: number | null;
}

interface SmsConfiguration { configured: boolean; apiKeySet: boolean; accountReferenceSet: boolean; accountReference: string; apiKeyMasked: string; defaultSenderId: string; credentialsTestedAt: string | null; callbackSecretSet: boolean; stripeWebhookSecretSet: boolean }
interface SenderRequest { id: number; storeId: number; storeName: string; senderId: string; status: string; rejectionReason: string | null; createdAt: string }

interface StoreSmsItem {
  store: { id: number; name: string };
  settings: {
    id: number; storeId: number; enabled: boolean;
    orderConfirmationEnabled: boolean; orderStatusUpdatesEnabled: boolean;
    marketingEnabled: boolean; smsBalancePence: number;
    sellPerSmsPenceOverride: number | null; minimumTopupPenceOverride: number | null; maximumDebtPenceOverride: number | null;
  } | null;
}

interface OptOut { id: number; phoneE164: string; reason: string | null; optedOutAt: string }

interface Campaign {
  id: number; title: string; message: string; storeId: number | null;
  target: string; segmentValue: string | null; status: string; totalSent: number; totalFailed: number;
  estimatedCostPence: number; actualCostPence: number; createdAt: string; sentAt: string | null;
}

interface AudienceSegment {
  key: string; label: string; description: string;
  parameter: { name: "town"; label: string } | null;
}

interface AudiencePreview {
  segment: { key: string; label: string; description: string };
  counts: {
    matchedUsers: number; optedOutUsers: number; invalidNumbers: number;
    suppressedNumbers: number; duplicatesDropped: number; uniqueRecipients: number;
  };
  message: { encoding: string; characters: number; segmentsPerMessage: number; billableSegments: number };
  cost: { known: boolean; pricePerSegmentMillipence: number | null; estimatedCostPence: number | null };
  balance: { availablePence: number; reservedPence: number; currency: string } | null;
  warnings: string[];
}

interface SmsLogEntry {
  id: number; toPhone: string; body: string; storeId: number | null;
  campaignId: number | null; type: string; status: string;
  costPence: number; twilioSid: string | null; errorText: string | null; createdAt: string;
}

interface Stats {
  last30Days: { sent: number; failed: number; optedOut: number; costPence: number };
  globalOptOuts: number;
  byType: Array<{ type: string; c: number }>;
}

export default function AdminSmsManagement() {
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("settings");

  // Platform settings
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [configuration, setConfiguration] = useState<SmsConfiguration | null>(null);
  const [credentialForm, setCredentialForm] = useState({ esendexApiKey: "", esendexAccountReference: "", defaultSenderId: "", callbackSecret: "", stripeWebhookSecret: "", testPhone: "" });
  const [senderRequests, setSenderRequests] = useState<SenderRequest[]>([]);

  // Store settings
  const [stores, setStores] = useState<StoreSmsItem[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);

  // Opt-outs
  const [optOuts, setOptOuts] = useState<OptOut[]>([]);
  const [optOutsLoading, setOptOutsLoading] = useState(false);
  const [optOutPage, setOptOutPage] = useState(1);
  const [optOutTotal, setOptOutTotal] = useState(0);
  const [newOptOutPhone, setNewOptOutPhone] = useState("");
  const [newOptOutReason, setNewOptOutReason] = useState("");
  const [addingOptOut, setAddingOptOut] = useState(false);

  // Campaigns
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [newCampaignTitle, setNewCampaignTitle] = useState("");
  const [newCampaignMsg, setNewCampaignMsg] = useState("");
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState<number | null>(null);
  // Audience segmentation + recipient-count preview
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [segmentKey, setSegmentKey] = useState("all");
  const [segmentTown, setSegmentTown] = useState("");
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Log
  const [log, setLog] = useState<SmsLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logStatusFilter, setLogStatusFilter] = useState("");

  // Stats
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    const res = await fetch("/api/admin/sms/settings", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as { settings: PlatformSettings; configuration: SmsConfiguration }; setSettings(d.settings); setConfiguration(d.configuration); setCredentialForm(v => ({ ...v, defaultSenderId: d.configuration.defaultSenderId ?? "", esendexAccountReference: d.configuration.accountReference ?? "" })); }
  }, []);

  const loadSenders = useCallback(async () => {
    const res = await fetch("/api/admin/sms/sender-ids", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as { items: SenderRequest[] }; setSenderRequests(d.items); }
  }, []);

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    const res = await fetch("/api/admin/sms/stores", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as { items: StoreSmsItem[] }; setStores(d.items); }
    setStoresLoading(false);
  }, []);

  const loadOptOuts = useCallback(async (page = 1) => {
    setOptOutsLoading(true);
    const res = await fetch(`/api/admin/sms/opt-outs?page=${page}`, { headers: authHeaders() });
    if (res.ok) {
      const d = await res.json() as { items: OptOut[]; total: number };
      setOptOuts(d.items); setOptOutTotal(d.total); setOptOutPage(page);
    }
    setOptOutsLoading(false);
  }, []);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    const res = await fetch("/api/admin/sms/campaigns", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as { items: Campaign[] }; setCampaigns(d.items); }
    setCampaignsLoading(false);
  }, []);

  const loadSegments = useCallback(async () => {
    const res = await fetch("/api/admin/sms/segments", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as { segments: AudienceSegment[] }; setSegments(d.segments); }
  }, []);

  const activeSegment = segments.find(s => s.key === segmentKey) ?? null;

  /**
   * Ask the API exactly how many people this campaign would reach. Read-only —
   * nothing is queued and nothing is sent.
   */
  const loadPreview = useCallback(async (key: string, town: string, message: string) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await fetch("/api/admin/sms/campaigns/preview", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ segment: key, town: town.trim() || null, message }),
      });
      const d = await res.json() as AudiencePreview & { error?: string };
      if (!res.ok) { setPreview(null); setPreviewError(d.error ?? "Failed to check audience"); return; }
      setPreview(d);
    } catch {
      setPreview(null);
      setPreviewError("Failed to check audience");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const loadLog = useCallback(async (page = 1, status = "") => {
    setLogLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set("status", status);
    const res = await fetch(`/api/admin/sms/log?${params}`, { headers: authHeaders() });
    if (res.ok) {
      const d = await res.json() as { items: SmsLogEntry[]; total: number };
      setLog(d.items); setLogTotal(d.total); setLogPage(page);
    }
    setLogLoading(false);
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    const res = await fetch("/api/admin/sms/stats", { headers: authHeaders() });
    if (res.ok) { const d = await res.json() as Stats; setStats(d); }
    setStatsLoading(false);
  }, []);

  useEffect(() => { if (tab === "settings") void loadSettings(); }, [tab, loadSettings]);
  useEffect(() => { if (tab === "stores") void loadStores(); }, [tab, loadStores]);
  useEffect(() => { if (tab === "senders") void loadSenders(); }, [tab, loadSenders]);
  useEffect(() => { if (tab === "opt-outs") void loadOptOuts(1); }, [tab, loadOptOuts]);
  useEffect(() => { if (tab === "campaigns") { void loadCampaigns(); void loadSegments(); } }, [tab, loadCampaigns, loadSegments]);

  // Keep the recipient count in step with the audience and the message, but
  // debounce so typing a message does not hammer the preview endpoint.
  useEffect(() => {
    if (tab !== "campaigns") return;
    if (activeSegment?.parameter && !segmentTown.trim()) { setPreview(null); setPreviewError(null); return; }
    const timer = setTimeout(() => { void loadPreview(segmentKey, segmentTown, newCampaignMsg); }, 500);
    return () => clearTimeout(timer);
  }, [tab, segmentKey, segmentTown, newCampaignMsg, activeSegment, loadPreview]);
  useEffect(() => { if (tab === "log") void loadLog(1, ""); }, [tab, loadLog]);
  useEffect(() => { if (tab === "stats") void loadStats(); }, [tab, loadStats]);

  if (isLoading) return null;
  if (!user || user.role !== "admin") return <Redirect to="/" />;

  async function saveSettings() {
    if (!settings) return;
    setSettingsSaving(true);
    const res = await fetch("/api/admin/sms/settings", {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ ...settings, ...Object.fromEntries(Object.entries(credentialForm).filter(([key, value]) => key !== "testPhone" && value.trim())) }),
    });
    if (res.ok) {
      const d = await res.json() as { settings: PlatformSettings; configuration?: SmsConfiguration };
      setSettings(d.settings);
      if (d.configuration) setConfiguration(d.configuration);
      setCredentialForm(v => ({ ...v, esendexApiKey: "", callbackSecret: "", stripeWebhookSecret: "" }));
      setSettingsDirty(false);
      toast({ title: "Settings saved" });
    } else {
      toast({ title: "Failed to save settings", variant: "destructive" });
    }
    setSettingsSaving(false);
  }

  async function addOptOut() {
    if (!newOptOutPhone.trim()) return;
    setAddingOptOut(true);
    const res = await fetch("/api/admin/sms/opt-outs", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ phone: newOptOutPhone.trim(), reason: newOptOutReason.trim() || undefined }),
    });
    setAddingOptOut(false);
    if (res.ok) {
      setNewOptOutPhone(""); setNewOptOutReason("");
      void loadOptOuts(optOutPage);
      toast({ title: "Opt-out added" });
    } else {
      const d = await res.json() as { error?: string };
      toast({ title: d.error ?? "Failed to add opt-out", variant: "destructive" });
    }
  }

  async function removeOptOut(id: number) {
    if (!confirm("Remove this opt-out? The number will receive SMS again.")) return;
    await fetch(`/api/admin/sms/opt-outs/${id}`, { method: "DELETE", headers: authHeaders() });
    void loadOptOuts(optOutPage);
    toast({ title: "Opt-out removed" });
  }

  async function createCampaign() {
    if (!newCampaignTitle.trim() || !newCampaignMsg.trim()) return;
    setCreatingCampaign(true);
    const res = await fetch("/api/admin/sms/campaigns", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        title: newCampaignTitle.trim(),
        message: newCampaignMsg.trim(),
        segment: segmentKey,
        town: segmentTown.trim() || null,
      }),
    });
    setCreatingCampaign(false);
    if (res.ok) {
      setNewCampaignTitle(""); setNewCampaignMsg("");
      void loadCampaigns();
      toast({ title: "Campaign created" });
    } else {
      const d = await res.json() as { error?: string };
      toast({ title: d.error ?? "Failed to create campaign", variant: "destructive" });
    }
  }

  async function sendCampaign(campaign: Campaign) {
    // Confirm against a freshly measured recipient count, not a stale one.
    const res0 = await fetch("/api/admin/sms/campaigns/preview", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ segment: campaign.target, town: campaign.segmentValue, message: campaign.message, storeId: campaign.storeId }),
    });
    const p = res0.ok ? await res0.json() as AudiencePreview : null;
    const costLine = p?.cost.known && p.cost.estimatedCostPence !== null
      ? `Estimated cost ${pence(p.cost.estimatedCostPence)}`
      : "Estimated cost unknown (no Esendex rate set)";
    const balanceLine = p?.balance ? `Esendex balance ${pence(p.balance.availablePence)}.` : "";
    const summary = p
      ? `Send "${campaign.title}" to ${p.counts.uniqueRecipients} unique numbers?\n\n${costLine}. ${balanceLine}\n\nThis cannot be undone.`
      : `Send "${campaign.title}" now? This cannot be undone.`;
    if (!confirm(summary)) return;

    setSendingCampaignId(campaign.id);
    const res = await fetch(`/api/admin/sms/campaigns/${campaign.id}/send`, { method: "POST", headers: authHeaders() });
    setSendingCampaignId(null);
    if (res.ok) {
      const d = await res.json() as { recipientCount: number; estimatedEsendexCostPence: number | null };
      const cost = d.estimatedEsendexCostPence !== null ? ` (est. ${pence(d.estimatedEsendexCostPence)})` : "";
      toast({ title: `Sending to ${d.recipientCount} unique numbers${cost}` });
      void loadCampaigns();
    } else {
      const d = await res.json() as { error?: string };
      toast({ title: d.error ?? "Failed to send campaign", variant: "destructive" });
    }
  }

  async function deleteCampaign(id: number) {
    if (!confirm("Delete this campaign?")) return;
    await fetch(`/api/admin/sms/campaigns/${id}`, { method: "DELETE", headers: authHeaders() });
    void loadCampaigns();
    toast({ title: "Campaign deleted" });
  }

  async function toggleStoreEnabled(storeId: number, currentEnabled: boolean) {
    await fetch(`/api/admin/sms/stores/${storeId}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ enabled: !currentEnabled }),
    });
    void loadStores();
  }

  async function topUpBalance(storeId: number, currentBalance: number) {
    const input = prompt(`Top-up balance for store ${storeId}.\nCurrent balance: ${pence(currentBalance)}\n\nEnter amount in £ to add:`);
    if (!input) return;
    const pounds = parseFloat(input);
    if (isNaN(pounds) || pounds <= 0) { toast({ title: "Invalid amount", variant: "destructive" }); return; }
    const addPence = Math.round(pounds * 100);
    const response=await postAdminAdjustment(`/api/admin/sms/stores/${storeId}/adjustment`,{amountPence:addPence,reason:'Manual admin SMS credit'},authHeaders());
    if(!response.ok){const result=await response.json().catch(()=>({}));toast({title:result.error??'SMS credit was not confirmed',variant:'destructive'});return;}
    void loadStores();
    toast({ title: `Added ${pence(addPence)} to store balance` });
  }

  const TABS: Array<{ id: Tab; label: string; icon: React.ComponentType<{ size?: number }> }> = [
    { id: "settings", label: "Platform Settings", icon: Settings },
    { id: "stats", label: "Stats", icon: BarChart2 },
    { id: "stores", label: "Store Settings", icon: Store },
    { id: "senders", label: "Sender IDs", icon: CheckCircle },
    { id: "campaigns", label: "Campaigns", icon: Send },
    { id: "opt-outs", label: "Opt-Outs", icon: Ban },
    { id: "log", label: "SMS Log", icon: FileText },
  ];

  function statusBadge(status: string) {
    const cls = status === "sent" ? "bg-green-100 text-green-700"
      : status === "failed" ? "bg-red-100 text-red-700"
      : status === "sending" ? "bg-blue-100 text-blue-700"
      : status === "opted_out" ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-600";
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare size={22} className="text-primary" />
            SMS Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configure SMS, manage opt-outs, run campaigns and review logs.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-slate-200 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 flex-shrink-0 ${tab === t.id ? "text-primary border-primary" : "text-slate-500 border-transparent hover:text-slate-700"}`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Platform Settings */}
        {tab === "settings" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
            {!settings ? (
              <div className="flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : (
              <>
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-slate-800">Esendex Configuration</h2>
                  <p className="text-sm text-slate-500">Credentials are encrypted at rest and never returned to the browser. Status: <strong className={configuration?.configured ? "text-green-600" : "text-amber-600"}>{configuration?.configured ? "Configured" : "Not configured"}</strong>{configuration?.credentialsTestedAt ? ` · tested ${fmtDate(configuration.credentialsTestedAt)}` : ""}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input type="password" className="border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder={configuration?.apiKeySet ? "API key saved · enter to replace" : "Esendex API key"} value={credentialForm.esendexApiKey} onChange={e => { setCredentialForm(v => ({ ...v, esendexApiKey: e.target.value })); setSettingsDirty(true); }} />
                    <input className="border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder="Account reference (e.g. EXUK0000000)" value={credentialForm.esendexAccountReference} onChange={e => { setCredentialForm(v => ({ ...v, esendexAccountReference: e.target.value })); setSettingsDirty(true); }} />
                    <input className="border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder="Default sender ID" maxLength={11} value={credentialForm.defaultSenderId} onChange={e => { setCredentialForm(v => ({ ...v, defaultSenderId: e.target.value })); setSettingsDirty(true); }} />
                    <input type="password" className="border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder={configuration?.callbackSecretSet ? "Esendex callback secret saved" : "Esendex callback secret"} value={credentialForm.callbackSecret} onChange={e => { setCredentialForm(v => ({ ...v, callbackSecret: e.target.value })); setSettingsDirty(true); }} />
                    <input type="password" className="border border-slate-200 rounded-xl px-3 py-2 text-sm sm:col-span-2" placeholder={configuration?.stripeWebhookSecretSet ? "SMS Stripe webhook secret saved" : "SMS Stripe webhook signing secret"} value={credentialForm.stripeWebhookSecret} onChange={e => { setCredentialForm(v => ({ ...v, stripeWebhookSecret: e.target.value })); setSettingsDirty(true); }} />
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600 space-y-1"><div>Esendex webhook: <code>{window.location.origin}/api/sms/esendex-callback</code></div><div>Stripe webhook: <code>{window.location.origin}/api/sms/stripe-webhook</code></div><div>Subscribe at Esendex to <code>sms-message-delivered</code>, <code>sms-message-failed</code>, <code>sms-message-received</code> and <code>sms-inbound-opted-out</code>, authenticating with header <code>x-sms-callback-secret</code>.</div></div>
                  <div className="flex gap-2"><input type="tel" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder="Test mobile number" value={credentialForm.testPhone} onChange={e => setCredentialForm(v => ({ ...v, testPhone: e.target.value }))} /><button className="rounded-xl border border-primary px-4 py-2 text-sm font-semibold text-primary" onClick={async () => { const r = await fetch("/api/admin/sms/test", { method: "POST", headers: authHeaders(), body: JSON.stringify({ phone: credentialForm.testPhone }) }); const d = await r.json() as { error?: string }; toast({ title: r.ok ? "Test SMS sent" : d.error ?? "Test failed", variant: r.ok ? "default" : "destructive" }); if (r.ok) void loadSettings(); }}>Send test</button></div>
                </div>

                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-slate-800">Pricing</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Cost per SMS (pence)</label>
                      <p className="text-xs text-slate-400 mb-1">Provider cost per segment</p>
                      <input
                        type="number" min={0} value={settings.costPerSmsPence}
                        onChange={e => { setSettings({ ...settings, costPerSmsPence: Number(e.target.value) }); setSettingsDirty(true); }}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Sell per SMS (pence)</label>
                      <p className="text-xs text-slate-400 mb-1">VAT-inclusive per segment</p>
                      <input
                        type="number" min={0} value={settings.sellPerSmsPence}
                        onChange={e => { setSettings({ ...settings, sellPerSmsPence: Number(e.target.value) }); setSettingsDirty(true); }}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <div><label className="text-sm font-medium text-slate-700">Minimum top-up (pence)</label><p className="text-xs text-slate-400 mb-1">Default £20.00</p><input type="number" min={100} value={settings.minimumTopupPence} onChange={e => { setSettings({ ...settings, minimumTopupPence: Number(e.target.value) }); setSettingsDirty(true); }} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" /></div>
                    <div><label className="text-sm font-medium text-slate-700">Maximum shop debt (pence)</label><p className="text-xs text-slate-400 mb-1">Default £100.00</p><input type="number" min={0} value={settings.maximumDebtPence} onChange={e => { setSettings({ ...settings, maximumDebtPence: Number(e.target.value) }); setSettingsDirty(true); }} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" /></div>
                  </div>
                  <p className="text-xs text-slate-400">Margin per SMS: {pence(Math.max(0, settings.sellPerSmsPence - settings.costPerSmsPence))}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Esendex rate (pence per segment)</label>
                      <p className="text-xs text-slate-400 mb-1">
                        What Esendex actually charge you per message part. Their API publishes no rate card, so take this from
                        your contract. Measured on this account: 3.71p. Leave blank and campaign previews will say
                        "cost unknown" instead of guessing.
                      </p>
                      <input
                        type="number" min={0.001} max={100} step={0.001} placeholder="Not set"
                        // Stored as integer millipence so 3.71p is exact; entered as pence for readability.
                        value={settings.esendexPricePerSegmentMillipence === null ? "" : settings.esendexPricePerSegmentMillipence / 1000}
                        onChange={e => {
                          const pencePerSegment = e.target.value === "" ? null : Number(e.target.value);
                          setSettings({
                            ...settings,
                            esendexPricePerSegmentMillipence: pencePerSegment === null || !Number.isFinite(pencePerSegment)
                              ? null
                              : Math.round(pencePerSegment * 1000),
                          });
                          setSettingsDirty(true);
                        }}
                        className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h2 className="text-base font-semibold text-slate-800">Feature Toggles</h2>
                  {[
                    { key: "enabled" as const, label: "SMS Platform Enabled", desc: "Master switch — disabling this prevents all SMS from being sent" },
                    { key: "orderConfirmationEnabled" as const, label: "Order Confirmation SMS", desc: "Send SMS when an order is confirmed" },
                    { key: "marketingEnabled" as const, label: "Marketing Campaigns", desc: "Allow stores to run SMS marketing blasts" },
                    { key: "smsVerificationEnabled" as const, label: "SMS Phone Verification", desc: "Send verification codes to phone numbers" },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <div className="text-sm font-medium text-slate-800">{label}</div>
                        <div className="text-xs text-slate-500">{desc}</div>
                      </div>
                      <button
                        onClick={() => { setSettings({ ...settings, [key]: !settings[key] }); setSettingsDirty(true); }}
                        className={`text-2xl transition-colors ${settings[key] ? "text-primary" : "text-slate-300"}`}
                      >
                        {settings[key] ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                      </button>
                    </div>
                  ))}
                </div>

                {settingsDirty && (
                  <button
                    onClick={() => void saveSettings()}
                    disabled={settingsSaving}
                    className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-60"
                  >
                    {settingsSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                    Save Settings
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Stats */}
        {tab === "stats" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={() => void loadStats()} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {statsLoading || !stats ? (
              <div className="flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: "Sent (30d)", value: stats.last30Days.sent, icon: CheckCircle, color: "text-green-600" },
                    { label: "Failed (30d)", value: stats.last30Days.failed, icon: XCircle, color: "text-red-600" },
                    { label: "Opted-Out Blocked", value: stats.last30Days.optedOut, icon: Ban, color: "text-amber-600" },
                    { label: "Cost (30d)", value: pence(stats.last30Days.costPence), icon: BarChart2, color: "text-blue-600" },
                  ].map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5">
                      <div className={`mb-2 ${color}`}><Icon size={18} /></div>
                      <div className="text-2xl font-bold text-slate-800">{value}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Global Opt-Out List</h3>
                    <div className="text-3xl font-bold text-slate-800">{stats.globalOptOuts}</div>
                    <div className="text-xs text-slate-500 mt-1">numbers opted out of all SMS</div>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Messages by Type (30d)</h3>
                    <div className="space-y-1.5">
                      {stats.byType.map(({ type, c }) => (
                        <div key={type} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-slate-600">{type.replace(/_/g, " ")}</span>
                          <span className="font-semibold text-slate-800">{Number(c)}</span>
                        </div>
                      ))}
                      {stats.byType.length === 0 && <p className="text-sm text-slate-400">No data yet</p>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Store Settings */}
        {tab === "stores" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {storesLoading ? (
              <div className="p-6 flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500 text-xs font-semibold">
                    <th className="text-left px-4 py-3">Store</th>
                    <th className="text-center px-3 py-3">SMS</th>
                    <th className="text-center px-3 py-3">Orders</th>
                    <th className="text-center px-3 py-3">Status</th>
                    <th className="text-center px-3 py-3">Marketing</th>
                    <th className="text-right px-4 py-3">Balance</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map(({ store, settings: s }) => {
                    const enabled = s?.enabled ?? false;
                    const balance = s?.smsBalancePence ?? 0;
                    return (
                      <tr key={store.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800">{store.name}</td>
                        <td className="px-3 py-3 text-center">
                          <button onClick={() => void toggleStoreEnabled(store.id, enabled)}
                            className={enabled ? "text-primary" : "text-slate-300"}>
                            {enabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                          </button>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {(s?.orderConfirmationEnabled && s?.orderStatusUpdatesEnabled) ? (
                            <CheckCircle size={14} className="text-green-500 mx-auto" />
                          ) : (
                            <XCircle size={14} className="text-slate-300 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {s?.orderStatusUpdatesEnabled ? (
                            <CheckCircle size={14} className="text-green-500 mx-auto" />
                          ) : (
                            <XCircle size={14} className="text-slate-300 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          {s?.marketingEnabled ? (
                            <CheckCircle size={14} className="text-green-500 mx-auto" />
                          ) : (
                            <XCircle size={14} className="text-slate-300 mx-auto" />
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${balance < 100 ? "text-red-600" : "text-slate-800"}`}>
                          {pence(balance)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => void topUpBalance(store.id, balance)}
                            className="text-xs text-primary hover:underline font-medium"
                          >
                            Credit
                          </button>
                          <button onClick={async () => { const price = prompt("Price per SMS segment in pence (blank uses platform default):", s?.sellPerSmsPenceOverride?.toString() ?? ""); if (price === null) return; const minimum = prompt("Minimum top-up in pence (blank uses platform default):", s?.minimumTopupPenceOverride?.toString() ?? ""); if (minimum === null) return; await fetch(`/api/admin/sms/stores/${store.id}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ sellPerSmsPenceOverride: price.trim() ? Number(price) : null, minimumTopupPenceOverride: minimum.trim() ? Number(minimum) : null }) }); void loadStores(); }} className="ml-2 text-xs text-slate-500 hover:underline">Pricing</button>
                        </td>
                      </tr>
                    );
                  })}
                  {stores.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No stores found</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {tab === "senders" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]"><thead><tr className="border-b border-slate-100 text-xs text-slate-500"><th className="text-left p-4">Shop</th><th className="text-left p-4">Sender ID</th><th className="text-left p-4">Status</th><th className="text-left p-4">Requested</th><th className="p-4" /></tr></thead>
              <tbody>{senderRequests.map(item => <tr key={item.id} className="border-b border-slate-50"><td className="p-4 font-medium">{item.storeName}</td><td className="p-4 font-mono">{item.senderId}</td><td className="p-4">{statusBadge(item.status)}</td><td className="p-4 text-xs text-slate-500">{fmtDate(item.createdAt)}</td><td className="p-4"><div className="flex justify-end gap-2">{item.status !== "active" && <button className="text-xs rounded-lg bg-green-100 px-3 py-1 text-green-700" onClick={async () => { await fetch(`/api/admin/sms/sender-ids/${item.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "active" }) }); void loadSenders(); }}>Approve</button>}<button className="text-xs rounded-lg bg-red-100 px-3 py-1 text-red-700" onClick={async () => { const reason = prompt("Reason for rejection or revocation:") ?? ""; await fetch(`/api/admin/sms/sender-ids/${item.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: item.status === "active" ? "revoked" : "rejected", rejectionReason: reason }) }); void loadSenders(); }}>{item.status === "active" ? "Revoke" : "Reject"}</button></div></td></tr>)}{senderRequests.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">No sender ID requests</td></tr>}</tbody>
            </table>
            </div>
          </div>
        )}

        {/* Opt-Outs */}
        {tab === "opt-outs" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Add Opt-Out</h3>
              <div className="flex flex-wrap gap-3">
                <input
                  type="text" placeholder="+44 7700 900123" value={newOptOutPhone}
                  onChange={e => setNewOptOutPhone(e.target.value)}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <input
                  type="text" placeholder="Reason (optional)" value={newOptOutReason}
                  onChange={e => setNewOptOutReason(e.target.value)}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  onClick={() => void addOptOut()} disabled={!newOptOutPhone.trim() || addingOptOut}
                  className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-60"
                >
                  {addingOptOut ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {optOutsLoading ? (
                <div className="p-6 flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-500 text-xs font-semibold">
                        <th className="text-left px-4 py-3">Phone</th>
                        <th className="text-left px-4 py-3">Reason</th>
                        <th className="text-left px-4 py-3">Opted Out</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {optOuts.map(o => (
                        <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-4 py-3 font-mono text-slate-800">{o.phoneE164}</td>
                          <td className="px-4 py-3 text-slate-500">{o.reason ?? "—"}</td>
                          <td className="px-4 py-3 text-slate-500">{fmtDate(o.optedOutAt)}</td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => void removeOptOut(o.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {optOuts.length === 0 && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No opt-outs</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                  {optOutTotal > 50 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">{optOutTotal} total</span>
                      <div className="flex gap-2">
                        <button disabled={optOutPage <= 1} onClick={() => void loadOptOuts(optOutPage - 1)}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronLeft size={16} /></button>
                        <span className="text-xs text-slate-500">Page {optOutPage}</span>
                        <button disabled={optOutPage * 50 >= optOutTotal} onClick={() => void loadOptOuts(optOutPage + 1)}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronRight size={16} /></button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Campaigns */}
        {tab === "campaigns" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">New Platform-Wide Campaign</h3>
              <input
                type="text" placeholder="Campaign title" value={newCampaignTitle}
                onChange={e => setNewCampaignTitle(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />

              {/* Audience picker */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Audience</label>
                <select
                  value={segmentKey}
                  onChange={e => { setSegmentKey(e.target.value); setSegmentTown(""); }}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {segments.length === 0 && <option value="all">Everyone</option>}
                  {segments.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                {activeSegment && <p className="text-xs text-slate-400">{activeSegment.description}</p>}
                {activeSegment?.parameter && (
                  <input
                    type="text" placeholder={activeSegment.parameter.label} value={segmentTown}
                    onChange={e => setSegmentTown(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                )}
              </div>

              <textarea
                rows={3} placeholder="SMS message (160 GSM chars per segment; one emoji drops it to 70)" value={newCampaignMsg}
                onChange={e => setNewCampaignMsg(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />

              {/* Recipient count + cost, BEFORE anything is sent */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Who this reaches</span>
                  <button
                    onClick={() => void loadPreview(segmentKey, segmentTown, newCampaignMsg)}
                    disabled={previewLoading}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={previewLoading ? "animate-spin" : ""} /> Refresh
                  </button>
                </div>

                {previewError && <p className="text-xs text-red-600">{previewError}</p>}
                {!preview && !previewError && (
                  <p className="text-xs text-slate-400">{previewLoading ? "Counting…" : "Pick an audience to see the recipient count."}</p>
                )}

                {preview && (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-slate-800">{preview.counts.uniqueRecipients.toLocaleString()}</span>
                      <span className="text-sm text-slate-500">unique numbers will be messaged</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      {[
                        ["Matched in segment", preview.counts.matchedUsers],
                        ["Opted out of marketing", preview.counts.optedOutUsers],
                        ["Unusable numbers", preview.counts.invalidNumbers],
                        ["On the suppression list", preview.counts.suppressedNumbers],
                        ["Duplicates removed", preview.counts.duplicatesDropped],
                      ].map(([label, value]) => (
                        <div key={label as string} className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                          <div className="text-slate-400">{label}</div>
                          <div className="text-slate-800 font-semibold">{(value as number).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                        <div className="text-slate-400">Encoding</div>
                        <div className="text-slate-800 font-semibold">{preview.message.encoding}</div>
                      </div>
                      <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                        <div className="text-slate-400">Parts per message</div>
                        <div className="text-slate-800 font-semibold">{preview.message.segmentsPerMessage}</div>
                      </div>
                      <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                        <div className="text-slate-400">Billable parts</div>
                        <div className="text-slate-800 font-semibold">{preview.message.billableSegments.toLocaleString()}</div>
                      </div>
                      <div className="bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                        <div className="text-slate-400">Estimated cost</div>
                        <div className="text-slate-800 font-semibold">
                          {preview.cost.known && preview.cost.estimatedCostPence !== null ? pence(preview.cost.estimatedCostPence) : "Unknown"}
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-slate-500">
                      Esendex balance:{" "}
                      <span className="font-semibold text-slate-800">
                        {preview.balance ? pence(preview.balance.availablePence) : "unavailable"}
                      </span>
                      {preview.balance && preview.balance.reservedPence > 0 && <> ({pence(preview.balance.reservedPence)} reserved)</>}
                    </div>

                    {preview.warnings.map(w => (
                      <div key={w} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                        <AlertCircle size={13} className="mt-px shrink-0" /> <span>{w}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{newCampaignMsg.length} chars</span>
                <button
                  onClick={() => void createCampaign()}
                  disabled={!newCampaignTitle.trim() || !newCampaignMsg.trim() || creatingCampaign || (!!activeSegment?.parameter && !segmentTown.trim())}
                  className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-60"
                >
                  {creatingCampaign ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Create Draft
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {campaignsLoading ? (
                <div className="p-6 flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-500 text-xs font-semibold">
                      <th className="text-left px-4 py-3">Title</th>
                      <th className="text-left px-4 py-3">Target</th>
                      <th className="text-center px-3 py-3">Status</th>
                      <th className="text-right px-3 py-3">Sent</th>
                      <th className="text-right px-3 py-3">Cost</th>
                      <th className="text-left px-4 py-3">Created</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{c.title}</div>
                          <div className="text-xs text-slate-400 truncate max-w-[200px]">{c.message}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {segments.find(s => s.key === c.target)?.label ?? c.target.replace(/_/g, " ")}
                          {c.segmentValue ? `: ${c.segmentValue}` : ""}
                          {c.storeId ? ` (store ${c.storeId})` : ""}
                        </td>
                        <td className="px-3 py-3 text-center">{statusBadge(c.status)}</td>
                        <td className="px-3 py-3 text-right text-slate-700">{c.totalSent}</td>
                        <td className="px-3 py-3 text-right text-slate-700">{pence(c.actualCostPence || c.estimatedCostPence)}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(c.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-end">
                            {c.status === "draft" && (
                              <button
                                onClick={() => void sendCampaign(c)}
                                disabled={sendingCampaignId === c.id}
                                className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium disabled:opacity-60"
                              >
                                {sendingCampaignId === c.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                Send
                              </button>
                            )}
                            {c.status !== "sending" && (
                              <button onClick={() => void deleteCampaign(c.id)} className="text-red-400 hover:text-red-600">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {campaigns.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No campaigns yet</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Log */}
        {tab === "log" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={logStatusFilter}
                onChange={e => { setLogStatusFilter(e.target.value); void loadLog(1, e.target.value); }}
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="opted_out">Opted Out</option>
                <option value="not_configured">Not Configured</option>
              </select>
              <button onClick={() => void loadLog(logPage, logStatusFilter)} className="text-slate-400 hover:text-slate-700">
                <RefreshCw size={14} />
              </button>
              <span className="text-xs text-slate-400 ml-auto">{logTotal} total</span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {logLoading ? (
                <div className="p-6 flex items-center gap-2 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-500 text-xs font-semibold">
                        <th className="text-left px-4 py-3">To</th>
                        <th className="text-left px-4 py-3">Message</th>
                        <th className="text-left px-3 py-3">Type</th>
                        <th className="text-center px-3 py-3">Status</th>
                        <th className="text-right px-3 py-3">Cost</th>
                        <th className="text-left px-4 py-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {log.map(l => (
                        <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">{l.toPhone}</td>
                          <td className="px-4 py-3 max-w-[220px]">
                            <div className="truncate text-xs text-slate-600">{l.body}</div>
                            {l.errorText && <div className="text-xs text-red-500 truncate">{l.errorText}</div>}
                          </td>
                          <td className="px-3 py-3 text-xs capitalize text-slate-500">{l.type.replace(/_/g, " ")}</td>
                          <td className="px-3 py-3 text-center">{statusBadge(l.status)}</td>
                          <td className="px-3 py-3 text-right text-xs text-slate-600">{l.costPence ? pence(l.costPence) : "—"}</td>
                          <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(l.createdAt)}</td>
                        </tr>
                      ))}
                      {log.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No messages logged</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                  {logTotal > 50 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">Page {logPage} of {Math.ceil(logTotal / 50)}</span>
                      <div className="flex gap-2">
                        <button disabled={logPage <= 1} onClick={() => void loadLog(logPage - 1, logStatusFilter)}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronLeft size={16} /></button>
                        <button disabled={logPage * 50 >= logTotal} onClick={() => void loadLog(logPage + 1, logStatusFilter)}
                          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronRight size={16} /></button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
